/**
 * Client helper for recording suspicious cross-tenant / access-control events
 * into public.security_events via the record_security_event RPC.
 *
 * Fire-and-forget — never throws. Designed to be wrapped around code paths
 * that already failed (403s, RLS denials, blocked storage downloads) so we
 * don't accidentally double-log into noisy success paths.
 */
import { supabase } from "@/integrations/supabase/client";

export type SecurityEventType =
  | "forbidden"
  | "app_events_blocked"
  | "storage_denied"
  | "cross_org_write"
  | "signature_invalid"
  | "replay_blocked"
  | "rate_limited"
  | "rls_violation"
  | "other";

export type SecurityEventInput = {
  event_type: SecurityEventType;
  severity?: "info" | "warning" | "critical";
  organization_id?: string | null;
  target_org_id?: string | null;
  source?: string | null;
  route?: string | null;
  metadata?: Record<string, unknown>;
};

export function recordSecurityEvent(input: SecurityEventInput): void {
  if (typeof window === "undefined") return;
  void supabase
    .rpc("record_security_event" as never, {
      _event_type: input.event_type,
      _severity: input.severity ?? "warning",
      _organization_id: input.organization_id ?? null,
      _target_org_id: input.target_org_id ?? null,
      _source: input.source ?? null,
      _route: input.route ?? (typeof location !== "undefined" ? location.pathname : null),
      _metadata: (input.metadata ?? {}) as never,
    } as never)
    .then(({ error }) => {
      if (error) console.warn("[security-events]", input.event_type, error.message);
    });
}

/** Heuristic: classify a PostgREST / supabase error as a security signal. */
export function classifySupabaseError(error: { code?: string; message?: string } | null | undefined):
  | SecurityEventType
  | null {
  if (!error) return null;
  const msg = (error.message ?? "").toLowerCase();
  if (error.code === "42501" || msg.includes("permission denied")) return "forbidden";
  if (msg.includes("row-level security") || msg.includes("violates row-level security")) return "rls_violation";
  if (msg.includes("not authorized")) return "forbidden";
  return null;
}
